import { Contract, Coin, TxnStdFee } from "./contract";
import { SecretNetworkClient } from "secretjs";

export type Uint128 = string;
export type Addr = string;
export interface CumulativePricesResponse {
  assets: Asset[];
  cumulative_prices: [AssetInfo, AssetInfo, Uint128][];
  total_share: Uint128;
  [k: string]: unknown;
}
export interface Asset {
  amount: Uint128;
  info: AssetInfo;
  [k: string]: unknown;
}
export interface AssetInfo {
  contract_addr?: Addr | null;
  contract_code_hash?: string | null;
  denom?: string | null;
  [k: string]: unknown;
}
export type ExecuteMsg = {
  receive: Snip20ReceiveMsg;
} | {
  post_initialize: {
    [k: string]: unknown;
  };
} | {
  provide_liquidity: {
    assets: Asset[];
    auto_stake?: boolean | null;
    receiver?: string | null;
    slippage_tolerance?: Decimal | null;
    [k: string]: unknown;
  };
} | {
  swap: {
    ask_asset_info?: AssetInfo | null;
    belief_price?: Decimal | null;
    max_spread?: Decimal | null;
    offer_asset: Asset;
    to?: string | null;
    [k: string]: unknown;
  };
} | {
  update_config: {
    params: Binary;
    [k: string]: unknown;
  };
} | {
  propose_new_owner: {
    expires_in: number;
    owner: string;
    [k: string]: unknown;
  };
} | {
  drop_ownership_proposal: {
    [k: string]: unknown;
  };
} | {
  claim_ownership: {
    [k: string]: unknown;
  };
};
export type Binary = string;
export type Decimal = string;
export interface Snip20ReceiveMsg {
  amount: Uint128;
  from: Addr;
  memo?: string | null;
  msg?: Binary | null;
  sender: Addr;
  [k: string]: unknown;
}
export interface InstantiateMsg {
  asset_infos: AssetInfo[];
  factory_addr: string;
  factory_hash: string;
  init_params?: Binary | null;
  prng_seed: Binary;
  token_code_hash: string;
  token_code_id: number;
  viewing_key: string;
  [k: string]: unknown;
}
export interface MigrateMsg {
  [k: string]: unknown;
}
export interface PairInfo {
  asset_infos: AssetInfo[];
  contract_addr: Addr;
  contract_code_hash: string;
  liquidity_token: Addr;
  liquidity_token_hash: string;
  pair_type: string;
  [k: string]: unknown;
}
export interface PoolResponse {
  assets: Asset[];
  total_share: Uint128;
  [k: string]: unknown;
}
export type QueryMsg = {
  pair: {
    [k: string]: unknown;
  };
} | {
  pool: {
    [k: string]: unknown;
  };
} | {
  config: {
    [k: string]: unknown;
  };
} | {
  share: {
    amount: Uint128;
    [k: string]: unknown;
  };
} | {
  simulation: {
    ask_asset_info?: AssetInfo | null;
    offer_asset: Asset;
    [k: string]: unknown;
  };
} | {
  reverse_simulation: {
    ask_asset: Asset;
    offer_asset_info?: AssetInfo | null;
    [k: string]: unknown;
  };
} | {
  cumulative_prices: {
    [k: string]: unknown;
  };
} | {
  query_compute_d: {
    [k: string]: unknown;
  };
};
export interface ReverseSimulationResponse {
  commission_amount: Uint128;
  offer_amount: Uint128;
  spread_amount: Uint128;
  [k: string]: unknown;
}
export interface SimulationResponse {
  commission_amount: Uint128;
  return_amount: Uint128;
  spread_amount: Uint128;
  [k: string]: unknown;
}
export interface PairReadOnlyInterface {
  pair: () => Promise<any>;
  pool: () => Promise<any>;
  config: () => Promise<any>;
  share: ({
    amount
  }: {
    amount: string;
  }) => Promise<any>;
  simulation: ({
    askAssetInfo,
    offerAsset
  }: {
    askAssetInfo?: AssetInfo;
    offerAsset: Asset;
  }) => Promise<any>;
  reverseSimulation: ({
    askAsset,
    offerAssetInfo
  }: {
    askAsset: Asset;
    offerAssetInfo?: AssetInfo;
  }) => Promise<any>;
  cumulativePrices: () => Promise<any>;
  queryComputeD: () => Promise<any>;
}
export class PairQueryContract extends Contract implements PairReadOnlyInterface {
  constructor(client: SecretNetworkClient, contractAddress: string, contractHash?: string) {
    super(client, contractAddress, contractHash);
    this.pair = this.pair.bind(this);
    this.pool = this.pool.bind(this);
    this.config = this.config.bind(this);
    this.share = this.share.bind(this);
    this.simulation = this.simulation.bind(this);
    this.reverseSimulation = this.reverseSimulation.bind(this);
    this.cumulativePrices = this.cumulativePrices.bind(this);
    this.queryComputeD = this.queryComputeD.bind(this);
  }

  pair = async (): Promise<any> => {
    return this.queryMsg({
      pair: {}
    });
  };
  pool = async (): Promise<any> => {
    return this.queryMsg({
      pool: {}
    });
  };
  config = async (): Promise<any> => {
    return this.queryMsg({
      config: {}
    });
  };
  share = async ({
    amount
  }: {
    amount: string;
  }): Promise<any> => {
    return this.queryMsg({
      share: {
        amount
      }
    });
  };
  simulation = async ({
    askAssetInfo,
    offerAsset
  }: {
    askAssetInfo?: AssetInfo;
    offerAsset: Asset;
  }): Promise<any> => {
    return this.queryMsg({
      simulation: {
        ask_asset_info: askAssetInfo,
        offer_asset: offerAsset
      }
    });
  };
  reverseSimulation = async ({
    askAsset,
    offerAssetInfo
  }: {
    askAsset: Asset;
    offerAssetInfo?: AssetInfo;
  }): Promise<any> => {
    return this.queryMsg({
      reverse_simulation: {
        ask_asset: askAsset,
        offer_asset_info: offerAssetInfo
      }
    });
  };
  cumulativePrices = async (): Promise<any> => {
    return this.queryMsg({
      cumulative_prices: {}
    });
  };
  queryComputeD = async (): Promise<any> => {
    return this.queryMsg({
      query_compute_d: {}
    });
  };
}
export interface PairInterface extends PairReadOnlyInterface {
  receive: ({
    userAddress,
    customFees,
    memo1,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo1?: string;
    transferAmount?: readonly Coin[];
  }, {
    amount,
    from,
    memo,
    msg,
    sender
  }: {
    amount: string;
    from: string;
    memo?: string;
    msg?: Binary;
    sender: string;
  }) => Promise<any>;
  postInitialize: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }) => Promise<any>;
  provideLiquidity: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    assets,
    autoStake,
    receiver,
    slippageTolerance
  }: {
    assets: Asset[];
    autoStake?: boolean;
    receiver?: string;
    slippageTolerance?: Decimal;
  }) => Promise<any>;
  swap: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    askAssetInfo,
    beliefPrice,
    maxSpread,
    offerAsset,
    to
  }: {
    askAssetInfo?: AssetInfo;
    beliefPrice?: Decimal;
    maxSpread?: Decimal;
    offerAsset: Asset;
    to?: string;
  }) => Promise<any>;
  updateConfig: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    params
  }: {
    params: string;
  }) => Promise<any>;
  proposeNewOwner: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    expiresIn,
    owner
  }: {
    expiresIn: number;
    owner: string;
  }) => Promise<any>;
  dropOwnershipProposal: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }) => Promise<any>;
  claimOwnership: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }) => Promise<any>;
}
export class PairContract extends PairQueryContract implements PairInterface {
  constructor(client: SecretNetworkClient, contractAddress: string, contractHash?: string) {
    super(client, contractAddress, contractHash);
    this.receive = this.receive.bind(this);
    this.postInitialize = this.postInitialize.bind(this);
    this.provideLiquidity = this.provideLiquidity.bind(this);
    this.swap = this.swap.bind(this);
    this.updateConfig = this.updateConfig.bind(this);
    this.proposeNewOwner = this.proposeNewOwner.bind(this);
    this.dropOwnershipProposal = this.dropOwnershipProposal.bind(this);
    this.claimOwnership = this.claimOwnership.bind(this);
  }

  receive = async ({
    userAddress,
    customFees,
    memo1,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo1?: string;
    transferAmount?: readonly Coin[];
  }, {
    amount,
    from,
    memo,
    msg,
    sender
  }: {
    amount: string;
    from: string;
    memo?: string;
    msg?: Binary;
    sender: string;
  }): Promise<any> => {
    return await this.executeMsg({
      receive: {
        amount,
        from,
        memo,
        msg,
        sender
      }
    }, userAddress, customFees, memo1, transferAmount);
  };
  postInitialize = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }): Promise<any> => {
    return await this.executeMsg({
      post_initialize: {}
    }, userAddress, customFees, memo, transferAmount);
  };
  provideLiquidity = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    assets,
    autoStake,
    receiver,
    slippageTolerance
  }: {
    assets: Asset[];
    autoStake?: boolean;
    receiver?: string;
    slippageTolerance?: Decimal;
  }): Promise<any> => {
    return await this.executeMsg({
      provide_liquidity: {
        assets,
        auto_stake: autoStake,
        receiver,
        slippage_tolerance: slippageTolerance
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  swap = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    askAssetInfo,
    beliefPrice,
    maxSpread,
    offerAsset,
    to
  }: {
    askAssetInfo?: AssetInfo;
    beliefPrice?: Decimal;
    maxSpread?: Decimal;
    offerAsset: Asset;
    to?: string;
  }): Promise<any> => {
    return await this.executeMsg({
      swap: {
        ask_asset_info: askAssetInfo,
        belief_price: beliefPrice,
        max_spread: maxSpread,
        offer_asset: offerAsset,
        to
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  updateConfig = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    params
  }: {
    params: string;
  }): Promise<any> => {
    return await this.executeMsg({
      update_config: {
        params
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  proposeNewOwner = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    expiresIn,
    owner
  }: {
    expiresIn: number;
    owner: string;
  }): Promise<any> => {
    return await this.executeMsg({
      propose_new_owner: {
        expires_in: expiresIn,
        owner
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  dropOwnershipProposal = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }): Promise<any> => {
    return await this.executeMsg({
      drop_ownership_proposal: {}
    }, userAddress, customFees, memo, transferAmount);
  };
  claimOwnership = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }): Promise<any> => {
    return await this.executeMsg({
      claim_ownership: {}
    }, userAddress, customFees, memo, transferAmount);
  };
}